import type { PortalProviderAPI } from '@atlaskit/editor-common/portal';
import type { ContextIdentifierProvider } from '@atlaskit/editor-common/provider-factory';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { EditorView, NodeView } from '@atlaskit/editor-prosemirror/view';

import {
	type AgentManagedExtensionPluginState,
	pluginKey,
} from '../../common/agentManagedExtensionPluginType';
import {
	getAgentManagedExtensionNodeView,
	rerenderNodeViewsForEditorView,
} from '../nodeviews/agentManagedExtensionNodeView';

import { createAutoOpenConfigOnInsertView } from './auto-open-view';
import {
	createAgentManagedCopyPasteProps,
	createAgentManagedCopyPasteView,
} from './copy-paste-identity';
import { createSelectedWhileConfigOpenView } from './selected-while-config-open-view';
import {
	applyAutoCloseOnSelectionChange,
	applyAutoOpenOnInsert,
	applyMeta,
	getMeta,
	initialState,
} from './state';

export interface CreatePluginArgs {
	cloudId?: string;
	contentType?: string;
	getContextIdentifierProvider?: () => ContextIdentifierProvider | undefined;
	nodeViewPortalProviderAPI: PortalProviderAPI;
}

export const createPlugin = (
	args: CreatePluginArgs,
): SafePlugin<AgentManagedExtensionPluginState> => {
	const copyPasteIdentity = createAgentManagedCopyPasteProps({
		cloudId: args.cloudId,
		contentType: args.contentType,
		getContextIdentifierProvider: args.getContextIdentifierProvider,
	});

	return new SafePlugin<AgentManagedExtensionPluginState>({
		key: pluginKey,
		appendTransaction: copyPasteIdentity.appendTransaction,
		props: {
			...copyPasteIdentity.props,
			nodeViews: {
				bodiedExtension: getAgentManagedExtensionNodeView({
					nodeViewPortalProviderAPI: args.nodeViewPortalProviderAPI,
				}) as unknown as (
					node: PMNode,
					view: EditorView,
					getPos: () => number | undefined,
				) => NodeView,
			},
		},
		state: {
			init: initialState,
			apply(tr, prev, oldState, newState) {
				const withMeta = applyMeta(prev, getMeta(tr), newState);
				const withAutoOpen = applyAutoOpenOnInsert(withMeta, tr, oldState, newState);
				return applyAutoCloseOnSelectionChange(withAutoOpen, tr, newState);
			},
		},
		view: (editorView) => {
			const autoOpen = createAutoOpenConfigOnInsertView(editorView);
			const copyPaste = createAgentManagedCopyPasteView(editorView, {
				cloudId: args.cloudId,
				contentType: args.contentType,
				getContextIdentifierProvider: args.getContextIdentifierProvider,
				pendingArchivedRemovalsByLocalId: copyPasteIdentity.pendingArchivedRemovalsByLocalId,
				pendingPasteByLocalId: copyPasteIdentity.pendingPasteByLocalId,
				readyArchiveLocalIds: copyPasteIdentity.readyArchiveLocalIds,
				readyPasteLocalIds: copyPasteIdentity.readyPasteLocalIds,
				readyReactivationLocalIds: copyPasteIdentity.readyReactivationLocalIds,
			});
			const selectedWhileConfigOpen = createSelectedWhileConfigOpenView(editorView);
			let prevEditable = editorView.editable;
			let prevPluginState = pluginKey.getState(editorView.state);

			return {
				destroy: () => {
					autoOpen.destroy();
				},
				update: (view) => {
					autoOpen.update();
					copyPaste.update(view);
					selectedWhileConfigOpen.update();

					const currentPluginState = pluginKey.getState(view.state);
					if (view.editable !== prevEditable || currentPluginState !== prevPluginState) {
						prevEditable = view.editable;
						prevPluginState = currentPluginState;
						rerenderNodeViewsForEditorView(view);
					}
				},
			};
		},
	});
};
